import * as Handlebars from 'handlebars';

/**
 * Flexible string masking helper.
 *
 * Usage in Handlebars:
 *   {{maskString value}}                          → Masks entire string with '*'
 *   {{maskString value "*" startIndex endIndex}}            → Masks from start index to end index
 *   {{maskString value "*" "start" 4}}            → Masks first 4 characters
 *   {{maskString value "*" "end" 2}}             → Masks last 2 characters
 *
 * Examples:
 *   {{maskString "12345678"}}                     → ********
 *   {{maskString "12345678" "*" 2 6}}             → 12****78
 *   {{maskString "12345678" "*" "start" 4}}       → ****5678
 *   {{maskString "12345678" "*" "end" 2}}         → 123456**
 *
 * Parameters:
 * @param value - The string or number to mask.
 * @param symbol - Character to use for masking (default: '*').
 * @param modeOrStart - Either "start", "end", or a numeric start index.
 * @param countOrEnd - If mode is "start"/"end", this is the count of chars to mask.
 *                     If mode is numeric, this is the end index (exclusive).
 */
export function registerMaskString() {
  Handlebars.registerHelper(
    'maskString',
    function (
      value: string | number | undefined | null,
      symbol?: string,
      modeOrStart?: string | number,
      countOrEnd?: number,
    ) {
      if (value === null || value === undefined) return '';

      const str = String(value);
      const length = str.length;
      const maskChar = typeof symbol === 'string' && symbol?.length > 0 ? symbol[0] : '*';

      let startIndex = 0;
      let endIndex = length;

      if (typeof modeOrStart === 'string') {
        if (modeOrStart.toLowerCase() === 'start') {
          const count = Number(countOrEnd) || 0;
          startIndex = 0;
          endIndex = Math.min(count, length);
        } else if (modeOrStart.toLowerCase() === 'end') {
          const count = Number(countOrEnd) || 0;
          startIndex = Math.max(length - count, 0);
          endIndex = length;
        }
      } else if (typeof modeOrStart === 'number') {
        startIndex = Number(modeOrStart);
        endIndex = countOrEnd !== undefined ? Number(countOrEnd) : length;
        if (Number.isNaN(startIndex) || startIndex < 0) startIndex = 0;
        if (Number.isNaN(endIndex) || endIndex > length) endIndex = length;
      }

      if (startIndex >= length || startIndex >= endIndex) return str;

      const maskLength = endIndex - startIndex;
      const mask = maskChar.repeat(maskLength);

      return str.slice(0, startIndex) + mask + str.slice(endIndex);
    },
  );
}
